import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from './base.service';
import { Router } from '@angular/router';
import { ResourcePushWeekTemplateHomeworkLessonSubType } from '../model/ResourcePushTemplate';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class SyllabusService extends BaseService {

  constructor(protected override http: HttpClient, protected override router: Router) {
    super(http, router);
  }
  flattenTree(tree: any): any {
    return _.flatMap(tree, (node) => {
      const { children, ...rest } = node;
      return [rest, ...this.flattenTree(children)];
    });
  }
  category(resource_type: string, query?: any) {
    let queryStr = '';
    if (query) {
      queryStr = `?${this.objectToQueryString(query)}`;
    }

    const url = `/syllabus/${resource_type}/category${queryStr}`;
    return super.get(url);
  }

  /**
   * 游戏模块题库「资源路径」级联选项：与练习管理列表同源（{@link category} 一次拉全树），
   * 仅保留最上两级（分类 → 大纲），第二级一律为叶子，须选到第二级。
   * 区别于练习列表级联的 nzChangeOnSelect 任意层级，此处由调用方设 nzChangeOnSelect=false。
   */
  getExercisePathCascaderOptionsTwoLevels(): Promise<
    Array<{
      id: number;
      name: string;
      children: Array<{ id: number; name: string; isLeaf: boolean }>;
    }>
  > {
    return this.category('exercise').then((bookAttrs: any) => {
      const syllabus = bookAttrs?.syllabus;
      if (!Array.isArray(syllabus)) {
        return [];
      }
      const out: Array<{
        id: number;
        name: string;
        children: Array<{ id: number; name: string; isLeaf: boolean }>;
      }> = [];
      for (const node of syllabus) {
        const rawKids = node.children;
        if (!Array.isArray(rawKids) || rawKids.length === 0) {
          continue;
        }
        const children = rawKids.map((c: { id: number; name: string }) => ({
          id: c.id,
          name: c.name,
          isLeaf: true,
        }));
        out.push({ id: node.id, name: node.name, children });
      }
      return out;
    });
  }
  checkNameExist(resource_type: string, query?: any) {
    let queryStr = '';
    if (query) {
      queryStr = `?${this.objectToQueryString(query)}`;
    }
    const url = `/syllabus/${resource_type}/check/name${queryStr}`;
    return super.get(url);
  }
  deleteItem(resource_type: string, data: any) {
    return this.post(`/syllabus/${resource_type}/item/delete`, data);
  }

  save(resource_type: string, data: any) {
    return this.post(`/syllabus/${resource_type}/item/save`, data);
  }
  seq(resource_type: string, seq: any) {
    return this.post(`/syllabus/${resource_type}/item/seq`, {seq});
  }

  moveFolder(resource_type: string, data: { id: number; pid: number }) {
    return this.post(`/syllabus/${resource_type}/item/move`, data);
  }


  
  private resourcesCache: { [key: string]: Promise<Array<{ id: number; name: string }>> } = {};

  getResources(resourceType: string, pid: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    const cacheKey = `${resourceType}-${pid}`;
    if (!this.resourcesCache[cacheKey]) {
      this.resourcesCache[cacheKey] = this.get(`/syllabus/${resourceType}/resources/${pid}`).finally(() => {
        delete this.resourcesCache[cacheKey];
      });
    }
    return this.resourcesCache[cacheKey];
  }

  getCoursewares(syllabusId: number, courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/list/${syllabusId}/${courseId}`);
  }

  getCoursewareResourceTypes(courseId: number): Promise<Array<{
    resourceType: string;
  }>> {
    return this.get(`/courseware/${courseId}/resource-types`);
  }

  getCoursewareResourceVideos(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/${courseId}/videos`);
  }

  getCoursewareResourceAudios(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/${courseId}/audios`);
  }

  getCoursewareResourceRecordAudios(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/${courseId}/record-audios`);
  }

  getCoursewareResourceExercises(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/${courseId}/exercises`);
  }

  getCoursewareResourceCartoons(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/${courseId}/cartoons`);
  }

  getBookCoursorwareResourceReadings(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/book/${courseId}/readings`);
  }

  getBookCoursorwareResourceRecordAudios(courseId: number): Promise<Array<{
    id: number;
    name: string;
  }>> {
    return this.get(`/courseware/book/${courseId}/record-audios`);
  }


  private resourceCache: { [key: string]: Promise<Array<{ id: number; name: string }>> } = {};

  getVideos(coursewareId: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `videos-${coursewareId}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/videos/${coursewareId}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  getAudios(coursewareId: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `audios-${coursewareId}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/audios/${coursewareId}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  getBooks(coursewareId: number, type: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `books-${coursewareId}-${type}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/books/${coursewareId}?type=${type}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  getCartoons(coursewareId: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `cartoons-${coursewareId}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/cartoons/${coursewareId}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  getExercises(coursewareId: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `exercises-${coursewareId}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/exercises/${coursewareId}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  getExerciseItems(exerciseId: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `exercise-items-${exerciseId}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/exercise-items/${exerciseId}`).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  /** 趣配音：获取难度(type=1)与分类(type=2)可选项，type=3 忽略 */
  getDubbingOptions(): Promise<{ difficulty: Array<{ id: number; name: string }>; category: Array<{ id: number; name: string }> }> {
    return this.get('/syllabus/dubbing/resources/0').then((res: any) => {
      const list = res?.data || res || [];
      const difficulty = list.filter((item: { type: number }) => item.type === 1).map((item: { id: number; name: string }) => ({ id: item.id, name: item.name }));
      const category = list.filter((item: { type: number }) => item.type === 2).map((item: { id: number; name: string }) => ({ id: item.id, name: item.name }));
      return { difficulty, category };
    });
  }

  /** 趣配音：根据难度 id 与分类 id 获取片段列表，展示用 【sname】name */
  getDubbingItems(level1Id: number, level2Id: number): Promise<Array<{ id: number; name: string }>> {
    const cacheKey = `dubbing-${level1Id}-${level2Id}`;
    if (!this.resourceCache[cacheKey]) {
      this.resourceCache[cacheKey] = this.get(`/syllabus/resources/dubbing/${level1Id}/${level2Id}`).then((res: any) => {
        const list = res?.data || res || [];
        return list.map((item: { id: number; name: string; sname?: string }) => ({
          id: item.id,
          name: item.sname ? `【${item.sname}】${item.name}` : item.name
        }));
      }).finally(() => {
        delete this.resourceCache[cacheKey];
      });
    }
    return this.resourceCache[cacheKey];
  }

  // 模板相关方法
  getTemplateList(syllabusId: number): Promise<Array<{ template_id: number; template_name: string; syllabus_id: number; template_desc?: string }>> {
    return this.get(`/syllabus/template/list?syllabusId=${syllabusId}`);
  }

  saveTemplate(data: { syllabus_id: number; template_id?: number; template_name: string; template_desc?: string }): Promise<any> {
    return this.post('/syllabus/template/save', data);
  }

  deleteTemplate(syllabusId: number, templateId: number): Promise<any> {
    return this.post('/syllabus/template/delete', { syllabus_id: syllabusId, template_id: templateId });
  }

}
